import classNames from "classnames";
import { ChatExpandToggle } from "components/chat/ChatExpandToggle";
import { ConditionalLink } from "components/conditionalLink";
import { Badge } from "components/design/badge";
import ToolTip from "components/design/toolTip";
import {
  BundledTaskDropdown,
  BundledTaskDropdownItem,
} from "components/BundledTaskDropdown/BundledTaskDropdown";
import { Button } from "components/DesignSystem/Button/Button";
import { TagColorType } from "components/DesignSystem/Tag/Tag";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { ArrowLeft } from "components/icons/ArrowLeft";
import { CaretDown } from "components/icons/CaretDown";
import { Seperator } from "components/icons/Chat/Seperator";
import { FolderOutline } from "components/icons/FolderOutline";
import { Headset } from "components/icons/Headset";
import { LoadingIcon } from "components/icons/LoadingIcon";
import { Pinned } from "components/icons/Pinned";
import { CalendarWithClockIcon } from "components/icons/CalendarWithClockIcon";
import { SmallRightArrow } from "components/icons/SmallRightArrow";
import dayjs from "dayjs";
import { Search } from "components/icons/Search";
import { SquareHalf } from "components/icons/SquareHalf";
import {
  BOOKS_CHANNEL_CUSTOM_TYPE,
  INKLE_CPA_TEAM_CHAT_TYPE,
  INKLE_FCA_TEAM_CHANNEL,
} from "constants/channelType";
import { KB_SEARCH_CHANNEL } from "constants/chatType";
import { ACCOUNT_MANAGER } from "constants/help";
import { ARCHIVED } from "constants/task";
import { useCurrentAppContext } from "contexts/CurrentAppContext";
import { TASK_STATE_TO_COLOR } from "dictionaries";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { MobileChannelHeader } from "components/ChannelHeader/MobileChannelHeader";
import { useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useDispatch, useSelector } from "react-redux";
import PrivateChatHeaderIcon from "static/images/PrivateChatHeaderLock.svg";
import { useGetTaskFromChannelIdQuery } from "store/apis/task";
import {
  openActionCentreSlider,
  setChannelInfoSliderActive,
  setMediaSliderActive,
  setScrollToAuditTrail,
} from "store/slices/channelInfoSlider";
import { setChannelPendingActionsActive } from "store/slices/channelPendingActions";
import { setChannelPinnedItemsActive } from "store/slices/channelPinnedItems";
import { removeToOpenChatUrl, setChannelTaskId } from "store/slices/chat";
import {
  collapseDashboardSidebar,
  expandDashboardSidebar,
} from "store/slices/dashboard";
import {
  closeMessageSearch,
  setMessageSearchActive,
} from "store/slices/messageSearch";
import { RootState } from "store/store";
import {
  useChannelPreviewInfo,
  useChannelStateContext,
} from "stream-chat-react";
import { DefaultStreamChatGenerics } from "stream-chat-react/dist/types/types";
import { GetFilteredStatusSection } from "components/home/TaxComponents/FilingCard";
import { AmendmentTag } from "components/Task/AmendmentTag/AmendmentTag";
import {
  buildClientProgressStepDetails,
  getClientStageLabel,
} from "utils/clientProgress";
import { TaskStateDropdownTag } from "components/tasks/taskForForeignCA/ForeignCATask";
import { Task } from "types/Models/task";
import { DISPLAY_DATE } from "constants/date";
import { STAGE_KEY_MISSING_INFORMATION } from "constants/clientProgressStageKeys";
import { getIsEtdHidden } from "utils/getIsEtdHidden";
import { FilingPendingItemsModal } from "components/home/TaxComponents/TasksOverview/FilingPendingItemsModal";
import { useCurrentChatGroupContext } from "hooks/useCurrentChatGroupContext";
import { AiToggle } from "components/ChannelHeader/AiToggle";
import { TaxYearChip } from "components/ChannelHeader/TaxYearChip";
import { useIsGroupV2Platform } from "hooks/useIsGroupV2Platform";
import { useGroupAiStatus } from "hooks/useGroupAiStatus";
import { getTaxYearPeriod } from "util/getTaxYearPeriod";
import { TAX_YEAR_TASK_KEYS } from "constants/taxYear";
import { US } from "constants/countryCodes";
import { useAnalytics } from "hooks/useAnalytics";
import {
  CHAT_HEADER_DETAILS_SLIDER_CLICKED,
  CHAT_HEADER_DOCUMENTS_CLICKED,
  CHAT_HEADER_SEARCH_CLICKED,
} from "constants/analyticsEvents";

const TaskLink = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>() => {
  const { channel } =
    useChannelStateContext<StreamChatGenerics>("ChannelHeader");
  const { displayTitle } = useChannelPreviewInfo({
    channel,
  });
  const { data: channelTask, isLoading } = useGetTaskFromChannelIdQuery(
    {
      channelId: channel.id!,
    },
    {
      skip: !channel.id,
      refetchOnMountOrArgChange: true,
    }
  );

  const { tasks } = channelTask || {};
  const isBundledTask = tasks?.some(({ is_bundle }) => is_bundle);
  const task =
    tasks?.find(({ is_parent_task }) => is_parent_task) ?? tasks?.[0];
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isLoading) {
      dispatch(setChannelTaskId(task?.uuid || ""));
    }
  }, [dispatch, isLoading, task?.uuid]);

  if (isLoading) {
    return (
      <span className="t-mr-1.5 t-flex t-origin-center t-animate-spin">
        <LoadingIcon />
      </span>
    );
  }

  if (isBundledTask) {
    const bundledTaskItems: BundledTaskDropdownItem[] = (tasks ?? []).map(
      ({ uuid, name, current_state, is_archived }) => {
        const stateType = is_archived
          ? ARCHIVED
          : current_state?.type || "Not Started";

        return {
          uuid,
          name,
          link: `/tax/filings/${uuid}`,
          stateLabel: is_archived
            ? "Archived"
            : current_state?.name || "Not Started",
          tagType:
            (TASK_STATE_TO_COLOR[
              stateType as keyof typeof TASK_STATE_TO_COLOR
            ] as TagColorType) || "gray",
        };
      }
    );

    return (
      <BundledTaskDropdown
        tasks={bundledTaskItems}
        triggerTooltip="Associated tasks"
        triggerLabel={
          <h2 className="t-m-0 t-mb-0.5 t-flex t-items-center t-gap-2 t-text-subtext-sm t-text-text-100">
            {displayTitle}
          </h2>
        }
      />
    );
  }

  return (
    <ConditionalLink to={task?.uuid ? `/filings/${task?.uuid}` : ""}>
      <h2
        className={classNames("t-m-0 t-text-subtext-sm t-text-text-100", {
          "hover:t-underline": Boolean(task?.uuid),
        })}
      >
        {displayTitle}
      </h2>
    </ConditionalLink>
  );
};

export const ChannelHeader = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>({
  isPrivateChannel,
  setPrivateChannel,
  backButton,
  actionsAvailable = true,
  showOnlyAdminMessage,
  setShowOnlyAdminMessage,
}: {
  isPrivateChannel: boolean;
  setPrivateChannel: () => void;
  backButton?: React.ReactNode;
  actionsAvailable?: boolean;
  showOnlyAdminMessage?: boolean;
  setShowOnlyAdminMessage: (val: boolean) => void;
}) => {
  const { channel } =
    useChannelStateContext<StreamChatGenerics>("ChannelHeader");
  const isGroupV2Platform = useIsGroupV2Platform();
  const { trackEvent } = useAnalytics();

  const { isCpa, isCustomer, isPersonalTaxUser } = useRoleBasedView();
  const isSmallScreen = useIsSmallScreen();
  const dispatch = useDispatch();
  const isSidebarExpanded = useSelector(
    (state: RootState) => state.dashboard.dashboardSidebarExpand
  );

  const isMessageSearchActive = useSelector(
    (store: RootState) => store.messageSearch.isActive
  );

  const { isOpen } = useSelector((state: RootState) => state.chat);
  const isChatExpanded = useSelector(
    (state: RootState) => state.chatExpanded.isExpanded
  );
  const isChannelSliderOpen = useSelector(
    (store: RootState) => store.channelInfoSlider.isActive
  );

  const isMediaSliderActive = useSelector(
    (store: RootState) => store.channelInfoSlider.isMediaSliderActive
  );

  const isChannelPendingActionSliderOpen = useSelector(
    (store: RootState) => store.channelPendingActions.isActive
  );

  const isChannelPinnedItemsSliderOpen = useSelector(
    (store: RootState) => store.channelPinnedItems.isActive
  );

  const { showAIChannels } = useSelector(
    (state: RootState) => state.chatSearch
  );

  const isKBChannel = channel?.data?.custom_type === KB_SEARCH_CHANNEL;

  const { data: channelTask } = useGetTaskFromChannelIdQuery(
    {
      channelId: channel.id!,
    },
    {
      skip: !channel.id,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    tasks,
    company_group: companyGroup,
    entity: taskEntity,
  } = channelTask || {};
  const task =
    tasks?.find(({ is_parent_task }) => is_parent_task) ?? tasks?.[0];

  const isTask = task?.uuid ? true : false;
  const isMissingInformation =
    task?.client_progress?.current_key === STAGE_KEY_MISSING_INFORMATION;
  const isEtdHidden = getIsEtdHidden(task?.client_progress?.current_key);

  const { isAiEnabled } = useGroupAiStatus(companyGroup?.uuid);

  const [isPendingItemsModalOpen, setIsPendingItemsModalOpen] = useState(false);

  const groupName = channel?.data?.group_name as string;
  const season = channel?.data?.season as string;
  const groupId = companyGroup?.uuid || (channel?.data?.group_uuid as string);
  const entityId = channel?.data?.entity_id as string;
  const { entities, assigned_agent } = useCurrentChatGroupContext();
  const channelEntity = entities.find((entity) => entity.uuid === entityId);
  const entity = taskEntity || channelEntity;
  const assignedAgent = assigned_agent?.name;
  const groupEntity = entities.find(({ uuid }) => uuid === entity?.uuid);
  const isTaxYearTaskChannel = TAX_YEAR_TASK_KEYS.includes(
    task?.base_task_key ?? ""
  );
  const isUsBooksChannel =
    channel?.data?.custom_type === BOOKS_CHANNEL_CUSTOM_TYPE &&
    groupEntity?.country_code === US;
  const showTaxYear = !isCustomer && (isTaxYearTaskChannel || isUsBooksChannel);
  const isSeasonVisible = isTask && Boolean(season);
  const entityTaxYear = showTaxYear
    ? getTaxYearPeriod(groupEntity?.tax_year_end)
    : null;

  const entityLink = isCustomer ? `/entities` : `/crm/${groupId}`;

  const { currentApp } = useCurrentAppContext();

  const documentLink = isCustomer
    ? `${currentApp.url}/documents`
    : `/crm/${groupId}/documents/${channelTask?.company_group?.documents_group_id}`;

  const isBundledTask = tasks?.some(({ is_bundle }) => is_bundle);

  const onSearchClick = () => {
    trackEvent(CHAT_HEADER_SEARCH_CLICKED);

    if (isMessageSearchActive) {
      dispatch(expandDashboardSidebar());
      return dispatch(closeMessageSearch());
    }

    if (isChannelSliderOpen) {
      dispatch(
        setChannelInfoSliderActive({
          isActive: false,
        })
      );
    }

    if (isChannelPendingActionSliderOpen) {
      dispatch(
        setChannelPendingActionsActive({
          isActive: false,
        })
      );
    }

    if (isChannelPinnedItemsSliderOpen) {
      dispatch(
        setChannelPinnedItemsActive({
          isActive: false,
        })
      );
    }

    dispatch(collapseDashboardSidebar());
    return dispatch(
      setMessageSearchActive({ setSideBarToExpandOnClose: isSidebarExpanded })
    );
  };

  const onSliderButtonClick = () => {
    trackEvent(CHAT_HEADER_DETAILS_SLIDER_CLICKED);

    if (isChannelSliderOpen) {
      dispatch(expandDashboardSidebar());
      return dispatch(
        setChannelInfoSliderActive({
          isActive: false,
        })
      );
    }

    if (isMessageSearchActive) {
      dispatch(closeMessageSearch());
    }

    if (isChannelPendingActionSliderOpen) {
      dispatch(
        setChannelPendingActionsActive({
          isActive: false,
        })
      );
    }

    if (isChannelPinnedItemsSliderOpen) {
      dispatch(
        setChannelPinnedItemsActive({
          isActive: false,
        })
      );
    }

    dispatch(collapseDashboardSidebar());
    return dispatch(
      setChannelInfoSliderActive({
        isActive: true,
        setSideBarToExpandOnClose: isSidebarExpanded,
      })
    );
  };

  const onEtdButtonClick = () => {
    if (!isChannelSliderOpen) {
      if (isMessageSearchActive) dispatch(closeMessageSearch());
      if (isChannelPendingActionSliderOpen)
        dispatch(setChannelPendingActionsActive({ isActive: false }));
      if (isChannelPinnedItemsSliderOpen)
        dispatch(setChannelPinnedItemsActive({ isActive: false }));
      dispatch(collapseDashboardSidebar());
      dispatch(
        setChannelInfoSliderActive({
          isActive: true,
          setSideBarToExpandOnClose: isSidebarExpanded,
        })
      );
    }
    dispatch(setScrollToAuditTrail(true));
  };

  const openPendingItemsModal = () => setIsPendingItemsModalOpen(true);

  const onDocumentsClick = () => {
    trackEvent(CHAT_HEADER_DOCUMENTS_CLICKED);
  };

  const onPinnedItemsSliderClick = () => {
    if (isChannelPinnedItemsSliderOpen) {
      dispatch(expandDashboardSidebar());
      return dispatch(
        setChannelPinnedItemsActive({
          isActive: false,
        })
      );
    }

    if (isMessageSearchActive) {
      dispatch(closeMessageSearch());
    }

    if (isMediaSliderActive) {
      dispatch(setMediaSliderActive(false));
    }

    if (isChannelSliderOpen) {
      dispatch(
        setChannelInfoSliderActive({
          isActive: false,
        })
      );
    }

    if (isChannelPendingActionSliderOpen) {
      dispatch(
        setChannelPendingActionsActive({
          isActive: false,
        })
      );
    }

    dispatch(collapseDashboardSidebar());
    return dispatch(
      setChannelPinnedItemsActive({
        isActive: true,
        setSideBarToExpandOnClose: isSidebarExpanded,
      })
    );
  };

  const taskStateType = task?.is_archived
    ? ARCHIVED
    : task?.current_state?.type || "Not Started";

  const taskState = task?.is_archived
    ? "Archived"
    : task?.current_state?.name || "Not Started";

  const pendingActionCount: number = channel?.data
    ?.pending_action_items_count as number;
  const pinnedMessageCount = channel?.state?.pinnedMessages?.length || 0;

  const isNonGroupChannel = [
    INKLE_FCA_TEAM_CHANNEL,
    INKLE_CPA_TEAM_CHAT_TYPE,
  ].includes(channel?.data?.custom_type as string);

  const isBooks = channel?.data?.custom_type === BOOKS_CHANNEL_CUSTOM_TYPE;
  const hasTaskStatus =
    isCustomer || task?.client_progress || task?.books_global_status;

  // On mobile the full-page chat thread uses a compact merged header.
  // The floating chat (isOpen) keeps the default header.
  if (isSmallScreen && !isOpen && !backButton) {
    return (
      <MobileChannelHeader
        isPrivateChannel={isPrivateChannel}
        setPrivateChannel={setPrivateChannel}
        showOnlyAdminMessage={showOnlyAdminMessage}
        setShowOnlyAdminMessage={setShowOnlyAdminMessage}
        actionsAvailable={actionsAvailable}
        isKBChannel={isKBChannel}
        documentLink={documentLink}
        pendingActionCount={pendingActionCount || 0}
        onSearchClick={onSearchClick}
        onDetailsClick={onSliderButtonClick}
        onPendingActionsClick={() => dispatch(openActionCentreSlider())}
      />
    );
  }

  return (
    <>
      <div className="gap t-flex t-gap-2 t-border-0 t-border-b t-border-solid t-border-b-neutral-10 t-max-h-[72px] t-bg-white t-px-6 t-py-3.5 t-leading-[normal]">
        <div className="t-flex t-flex-col t-justify-center t-gap-2">
          <div className="t-flex t-h-[18px] t-gap-2.5 t-items-center">
            {isOpen && !backButton && (
              <button
                className="all:unset -t-mt-[3px]"
                onClick={(e) => {
                  dispatch(removeToOpenChatUrl());
                }}
              >
                <ArrowLeft size="16px" />
              </button>
            )}
            {backButton}
            <TaskLink />
            {showAIChannels && !isKBChannel && (
              <ToolTip
                text={
                  showOnlyAdminMessage
                    ? "View customer–AI interactions (read only)"
                    : "Switch to the admin-only Inkle AI view"
                }
              >
                <span>
                  <Switch
                    checked={Boolean(showOnlyAdminMessage)}
                    onCheckedChange={setShowOnlyAdminMessage}
                    size="small"
                  />
                </span>
              </ToolTip>
            )}
            {!isCustomer &&
              !isNonGroupChannel &&
              !showAIChannels &&
              !isKBChannel && (
                <Switch
                  checked={isPrivateChannel}
                  onCheckedChange={setPrivateChannel}
                  size="small"
                />
              )}
          </div>
          {(entity ||
            (!isCustomer && groupName) ||
            (isTask && season) ||
            (isTask && !isBundledTask) ||
            (isCpa && assignedAgent)) && (
            <span className="t-flex t-flex-wrap t-items-center t-gap-2">
              {!isPersonalTaxUser && (
                <>
                  <ConditionalLink
                    to={entityLink}
                    className="t-flex t-items-center t-text-body-sm t-text-text-30 after:t-ml-2 hover:!t-underline"
                  >
                    {!isCustomer && groupName && (
                      <>
                        <span className="t-flex t-items-center t-text-body-sm">
                          {groupName}
                        </span>
                        {groupName && entity && <span>&nbsp;-&nbsp;</span>}
                      </>
                    )}
                    {entity && (
                      <>
                        {Boolean(entity?.code_alpha_2) && (
                          <ReactCountryFlag
                            style={{
                              width: "14px",
                              height: "10px",
                              marginRight: "4px",
                            }}
                            svg
                            countryCode={entity?.code_alpha_2!}
                          />
                        )}
                        <span className="t-max-w-[120px] t-truncate">
                          {entity.name}
                        </span>
                      </>
                    )}
                  </ConditionalLink>
                  {entityTaxYear && !isSeasonVisible && (
                    <TaxYearChip taxYear={entityTaxYear} />
                  )}
                </>
              )}

              {isSeasonVisible && (
                <>
                  <span className="t-flex t-items-center">
                    <Seperator />
                  </span>
                  <span className="t-flex t-items-center t-gap-1">
                    <span className="t-text-body-sm t-text-neutral">
                      {season}
                    </span>
                    {entityTaxYear && <TaxYearChip taxYear={entityTaxYear} />}
                  </span>
                </>
              )}

              {isCpa && assignedAgent && (
                <>
                  <span className="t-flex t-items-center">
                    <Seperator />
                  </span>
                  <ToolTip text={ACCOUNT_MANAGER}>
                    <Button
                      size="small"
                      customType="ghost"
                      className="!t-h-auto !t-px-1"
                      onClick={onSliderButtonClick}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <div className="t-flex t-items-center t-gap-x-1">
                        <Headset color="grey" />
                        <div className="t-text-body-sm t-text-text-30">
                          {assignedAgent}
                        </div>
                      </div>
                    </Button>
                  </ToolTip>
                </>
              )}

              {isCustomer &&
                Boolean(task?.assigned_to?.name || assignedAgent) && (
                  <>
                    <span className="t-flex t-items-center">
                      <Seperator />
                    </span>
                    <ToolTip text={ACCOUNT_MANAGER}>
                      <Button
                        size="small"
                        customType="ghost"
                        className="!t-h-auto !t-px-1"
                        onClick={onSliderButtonClick}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <div className="t-flex t-items-center t-gap-x-1">
                          <Headset color="grey" />
                          <div className="t-text-body-sm t-text-text-30">
                            {task?.assigned_to?.name || assignedAgent}
                          </div>
                        </div>
                      </Button>
                    </ToolTip>
                  </>
                )}

              {isTask && !isBooks && hasTaskStatus && (
                <>
                  <span className="t-flex t-items-center">
                    <Seperator />
                  </span>

                  {!isCustomer ? (
                    <TaskStateDropdownTag task={task as unknown as Task} />
                  ) : task?.client_progress ? (
                    <div className="t-flex t-items-center t-gap-1.5">
                      {(isMissingInformation ||
                        (task.latest_etd && !isEtdHidden)) && (
                        <>
                          <ToolTip
                            propClass="t-w-72"
                            text={
                              isMissingInformation ? (
                                <div className="t-space-y-1.5 t-text-left">
                                  <div className="t-text-body-sm t-text-white">
                                    We&apos;ll update the estimated draft date
                                    once we receive the required information
                                    from you.
                                  </div>
                                  <Button
                                    type="button"
                                    customType="link"
                                    size="small"
                                    onClick={openPendingItemsModal}
                                  >
                                    <span className="t-flex t-items-center t-gap-1 t-text-purple-40">
                                      View pending items
                                      <SmallRightArrow />
                                    </span>
                                  </Button>
                                </div>
                              ) : (
                                <div className="t-space-y-0.5">
                                  <div className="t-text-body-sm t-text-white t-text-left">
                                    Draft expected by this date.
                                    <br />
                                    Click to view all updates.
                                  </div>
                                </div>
                              )
                            }
                          >
                            <Button
                              size="small"
                              customType="ghost"
                              onClick={
                                isMissingInformation
                                  ? openPendingItemsModal
                                  : onEtdButtonClick
                              }
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              <span className="t-text-text-30 hover:t-text-text-60 t-flex t-gap-1 t-items-center">
                                <CalendarWithClockIcon />
                                <span
                                  className={classNames("t-text-body-sm", {
                                    "t-italic": isMissingInformation,
                                  })}
                                >
                                  {isMissingInformation
                                    ? "Pending on you"
                                    : dayjs(task.latest_etd).format(
                                        DISPLAY_DATE
                                      )}
                                </span>
                              </span>
                            </Button>
                          </ToolTip>
                          <Seperator />
                        </>
                      )}
                      <GetFilteredStatusSection
                        status={getClientStageLabel({
                          key: task.client_progress.current_key,
                          label: task.client_progress.current_label,
                        })}
                        totalSteps={task.total_task_states}
                        finishedSteps={task.completed_task_states}
                        stepDetails={buildClientProgressStepDetails(
                          task.client_progress
                        )}
                        isActionPending={false}
                        showProgress
                        latestEtd={task.latest_etd ?? null}
                        taskUuid={task.uuid ?? null}
                        previousCycles={task.client_progress.previous_cycles}
                      />
                      <AmendmentTag
                        amendmentNumber={task.client_progress.amendment_number}
                      />
                      {isMissingInformation && task.uuid && (
                        <FilingPendingItemsModal
                          open={isPendingItemsModalOpen}
                          onOpenChange={setIsPendingItemsModalOpen}
                          taskUuid={task.uuid}
                          filingName={task.name}
                        />
                      )}
                    </div>
                  ) : (
                    <Badge
                      color={TASK_STATE_TO_COLOR[taskStateType]}
                      size="small"
                    >
                      {taskState}
                    </Badge>
                  )}
                </>
              )}
            </span>
          )}
        </div>

        {(isChatExpanded ||
          (actionsAvailable && !showAIChannels && !isKBChannel)) && (
          <div className="t-flex t-gap-3 t-ml-auto t-items-center">
            {actionsAvailable && !showAIChannels && !isKBChannel && (
              <div className="t-flex t-gap-1 t-items-center">
                {isGroupV2Platform && isAiEnabled && <AiToggle />}
                <Button
                  size="xsmall"
                  customType="ghost_icon"
                  onClick={onSearchClick}
                >
                  <Search />
                </Button>
                {Boolean(pinnedMessageCount) && (
                  <Button
                    size="xsmall"
                    customType="ghost_icon"
                    onClick={onPinnedItemsSliderClick}
                  >
                    <div className="t-text-text-30 t-flex t-items-center t-gap-1">
                      <Pinned />
                      <span className="t-text-text-30 t-text-body-sm">
                        {pinnedMessageCount! > 99 ? "99+" : pinnedMessageCount}
                      </span>
                    </div>
                  </Button>
                )}
                <ConditionalLink
                  className="t-flex t-items-center"
                  to={documentLink}
                  onClick={onDocumentsClick}
                >
                  <Button size="xsmall" customType="ghost_icon">
                    <FolderOutline size="16" className="t-text-text-60" />
                  </Button>
                </ConditionalLink>
                <Button
                  size="xsmall"
                  customType="ghost_icon"
                  onClick={onSliderButtonClick}
                >
                  <span className="t-text-text-30">
                    <SquareHalf />
                  </span>
                </Button>
              </div>
            )}
            {isChatExpanded && (
              <>
                <Seperator />
                <ChatExpandToggle />
              </>
            )}
          </div>
        )}
      </div>
      {isPrivateChannel && (
        <div className="t-flex t-items-center t-justify-center t-bg-dark_green-30 t-p-1.5">
          <img
            className="t-h-3.5"
            src={PrivateChatHeaderIcon}
            alt="private chat icon"
          />
          <span className="t-text-subtext-sm t-text-dark_green-90">
            &nbsp; This is a private chat
          </span>
        </div>
      )}
      {Boolean(pendingActionCount) && pendingActionCount > 0 && (
        <div
          onClick={() => dispatch(openActionCentreSlider())}
          className="t-flex t-items-center t-justify-center t-bg-orange-0 t-p-1.5 t-gap-2.5"
          role="button"
        >
          <span className="t-text-subtext-sm t-text-orange-80">
            Actions Pending
          </span>
          <span className="t-text-orange-80 -t-rotate-90">
            <CaretDown />
          </span>
        </div>
      )}
    </>
  );
};
